<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from 'next/message/bubbles/Base.vue';
import MessageMeta from '../MessageMeta.vue';
import FormattedContent from './Text/FormattedContent.vue';
import { useMessageContext } from '../provider.js';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants.js';

const { content, contentAttributes, variant, orientation } =
  useMessageContext();

const { t } = useI18n();

// Meta's Click-To-Direct payload, stored verbatim, so the field names here are
// Instagram's own rather than the CTWA vocabulary WhatsAppAdReferral.vue uses.
// MessageList excludes this key from the camelCase transform for that reason.
const referral = computed(() => contentAttributes.value?.igAdReferral || {});

const context = computed(() => referral.value.ads_context_data || {});
const adTitle = computed(() => context.value.ad_title);
const adId = computed(() => referral.value.ad_id);
const reference = computed(() => referral.value.ref);

// photo_url is the image the customer tapped; video_url is a thumbnail for a
// video ad, so either is usable as the preview.
const previewUrl = computed(
  () => context.value.photo_url || context.value.video_url
);

const metaAlignClass = computed(() =>
  orientation.value === ORIENTATION.RIGHT ? 'justify-end' : 'justify-start'
);

const metaColorClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.PRIVATE
    ? 'text-n-amber-12/50'
    : 'text-n-slate-11'
);
</script>

<template>
  <div class="flex flex-col max-w-80">
    <BaseBubble
      hide-meta
      class="p-0 overflow-hidden"
      data-bubble-name="instagram-ad-referral"
    >
      <div class="flex flex-col">
        <div
          class="flex flex-col gap-1 px-3 pt-2.5 border-l-2 border-n-blue-8 bg-n-alpha-1"
        >
          <span class="text-xs font-medium text-n-blue-11">
            {{ t('CONVERSATION.INSTAGRAM_AD_REFERRAL.FROM_AD') }}
          </span>
          <img
            v-if="previewUrl"
            :src="previewUrl"
            :alt="adTitle || ''"
            class="object-cover w-full rounded max-h-32 bg-n-alpha-2"
          />
          <span v-if="adTitle" class="text-sm break-words text-n-slate-12">
            {{ adTitle }}
          </span>
          <div class="flex flex-wrap gap-1 pb-2">
            <span
              v-if="adId"
              class="px-2 py-0.5 font-mono text-xs rounded bg-n-alpha-2 text-n-slate-11"
            >
              {{ t('CONVERSATION.INSTAGRAM_AD_REFERRAL.AD_ID', { id: adId }) }}
            </span>
            <span
              v-if="reference"
              class="px-2 py-0.5 font-mono text-xs rounded bg-n-alpha-2 text-n-slate-11"
            >
              {{ reference }}
            </span>
          </div>
        </div>

        <div class="px-3 py-2.5">
          <FormattedContent v-if="content" :content="content" />
          <MessageMeta :class="[metaAlignClass, metaColorClass]" class="mt-2" />
        </div>
      </div>
    </BaseBubble>
  </div>
</template>
